import { QuestionnaireSchedule, QuestionnaireScheduleDefinition } from '@stores/QuestionnaireAdminStore';

/**
 * A timepoint row in the schedule editor. Days stay as typed until saving; an empty window means it never closes.
 * opensOnDay is the program day it opens, where day 1 is the cohort's start date.
 */
export interface ScheduleTimepointRow {
  timepointId: number | null;
  opensOnDay: string;
  windowDays: string;
}

/** A schedule as the schedule editor holds it while the admin types. */
export interface ScheduleEdit {
  code: string;
  name: string;
  rows: ScheduleTimepointRow[];
}

export const emptyTimepointRow = (): ScheduleTimepointRow => ({ timepointId: null, opensOnDay: '', windowDays: '' });

export const emptyScheduleEdit = (): ScheduleEdit => ({ code: '', name: '', rows: [emptyTimepointRow()] });

export const scheduleEditFrom = (schedule: QuestionnaireSchedule): ScheduleEdit => ({
  code: schedule.code,
  name: schedule.name,
  rows: schedule.timepoints.map((timepoint) => ({
    timepointId: timepoint.timepointId,
    opensOnDay: String(timepoint.opensOnDay),
    windowDays: timepoint.windowDays === null ? '' : String(timepoint.windowDays),
  })),
});

const isWholeNumber = (value: string, minimum: number): boolean =>
  /^\d+$/.test(value.trim()) && Number(value) >= minimum;

/** What has to be fixed before the schedule can be saved. Empty when it's ready. */
export const scheduleProblems = (edit: ScheduleEdit): string[] => {
  const problems: string[] = [];
  if (!edit.code.trim()) problems.push('Add a code');
  if (!edit.name.trim()) problems.push('Add a name');
  if (!edit.rows.length) problems.push('Add at least one timepoint');
  if (edit.rows.some((row) => row.timepointId === null)) problems.push('Choose a timepoint for every row');
  const chosen = edit.rows.map((row) => row.timepointId).filter((timepointId) => timepointId !== null);
  if (new Set(chosen).size < chosen.length) problems.push('Use each timepoint only once');
  if (edit.rows.some((row) => !isWholeNumber(row.opensOnDay, 1))) {
    problems.push('Opens on day must be a whole number, 1 or more');
  }
  if (edit.rows.some((row) => row.windowDays.trim() && !isWholeNumber(row.windowDays, 1))) {
    problems.push('Open for must be whole days, 1 or more, or empty for never closes');
  }
  return problems;
};

/** The schedule to save, with timepoints in the order they open. Only call once scheduleProblems is empty. */
export const scheduleDefinitionFrom = (edit: ScheduleEdit): QuestionnaireScheduleDefinition => ({
  code: edit.code.trim(),
  name: edit.name.trim(),
  timepoints: edit.rows
    .map((row) => ({
      timepointId: row.timepointId ?? 0,
      opensOnDay: Number(row.opensOnDay),
      windowDays: row.windowDays.trim() ? Number(row.windowDays) : null,
    }))
    .sort((first, second) => first.opensOnDay - second.opensOnDay),
});

/** The program day a timepoint opens and how long it stays open, e.g. "Day 36, open 10 days". */
export const describeTiming = (opensOnDay: number, windowDays: number | null): string =>
  `Day ${opensOnDay}, ${windowDays === null ? 'never closes' : `open ${windowDays} ${windowDays === 1 ? 'day' : 'days'}`}`;
